import React from 'react';
import { triggerEnhancedWebviewFeature } from '@buzzfeed/bf-utils/lib/webview';
import { addParams } from '@buzzfeed/bf-utils/lib/query-string';
import { useTrackingContext, useViewImpression } from '../../../hooks';
import createIcon from './create.svg';
import styles from './index.module.scss';
import pixelLoader from '../../../public/static/images/pixel-loader.gif';
import Package from '../Package';

const Item = ({
  index,
  item,
  resolveItemType,
  contentObject,
  isWeb,
  onWebNavigate,
  currentZoneName,
  zoneOrder,
  packagesCount = 0,
}) => {
  const { trackInternalLink } = useTrackingContext();
  const idKey = item?.id != null ? String(item.id) : undefined;
  const itemType = resolveItemType(item) || 'bfp_content';
  const isPackage = itemType === 'package';

  const imageUrl =
    contentObject?.content?.single_comment?.picture_url ||
    contentObject?.content?.bfp?.data?.intro?.card?.backgroundImage?.url ||
    contentObject?.content?.bfp?.data?.promo?.card?.image_url ||
    '';

  const rawTitle =
    contentObject?.title ||
    contentObject?.content?.bfp?.data?.intro?.card?.title ||
    contentObject?.content?.bfp?.data?.intro?.card?.headline ||
    contentObject?.content?.single_comment?.content_title ||
    contentObject?.content?.single_comment?.blurb ||
    item?.title ||
    `Content ${item?.id ?? index}`;

  const creationCount =
    contentObject?.content?.comments?.by_type?.ai_generation?.count;

  const title =
    rawTitle && rawTitle.length > 80 ? `${rawTitle.slice(0, 77)}...` : rawTitle;

  const isLoaded = Boolean(contentObject);

  // Base tracking (common to both web and app)
  const trackingData = {
    unit_type: 'feed',
    unit_name: `${currentZoneName}|${zoneOrder}`,
    subunit_type: 'component',
    subunit_name: `${itemType}|${item?.id}`,
    item_type: 'card',
    item_name: item?.id,
    position_in_unit: packagesCount + index,
    position_in_subunit: 0,
    target_content_type: 'widget',
    target_content_id: item?.id,
  };

  // Web-specific overrides
  if (isWeb) {
    trackingData.position_in_unit = currentZoneName === 'all' ? packagesCount + index : index;
  }

  // View impression tracking
  const { setObservable } = useViewImpression({
    trackingData,
  });

  const onItemNavigate = ({ type, id }) => {
    trackInternalLink(trackingData);

    if (isWeb) {
      onWebNavigate(type, id);
      return;
    }
    triggerEnhancedWebviewFeature({
      feature: 'navigate',
      params: {
        path: `/webview/feed/content-objects/${type}/${id}`,
        queryParams: {
          // match what is currently sent from same navigation in app
          source: 'page',
          type: 'creation',
        },
      },
      version: 1,
    });
  };

  const getFormattedCount = (count) => {
    if (count > 999) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count;
  };

  // Package items render via Package component
  if (isPackage) {
    return (
      <Package
        packageData={contentObject}
        index={index}
        isWeb={isWeb}
        onWebNavigate={onWebNavigate}
        currentZoneName={currentZoneName}
        zoneOrder={zoneOrder}
      />
    );
  }

  // Standard item rendering
  return (
    <div
      key={idKey || index}
      className={`${styles.item} ${!isLoaded ? styles.loadingItem : ''} ${isWeb ? styles.web : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => onItemNavigate({ type: itemType, id: item.id })}
      aria-label={title}
      ref={(el) => setObservable(el)}
    >
      {imageUrl && (
        <img
          src={addParams(imageUrl, { resize: '650:*' })}
          alt=""
          className={styles.itemImage}
          loading="lazy"
        />
      )}
      {!isLoaded ? (
        <img src={pixelLoader} className={styles.loading} alt="Loading..." />
      ) : (
        <div className={styles.itemContent}>
          <span className={styles.itemTitle}>{title}</span>
          <div className={styles.generationCount}>
            <img src={createIcon} />
            <div>{getFormattedCount(creationCount)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoize to prevent unnecessary re-renders
// Only re-render if index, item.id, or contentObject actually change
export default React.memo(Item, (prevProps, nextProps) => {
  return (
    prevProps.index === nextProps.index &&
    prevProps.item?.id === nextProps.item?.id &&
    prevProps.contentObject === nextProps.contentObject
  );
});
